import { useCallback, useState } from 'react';
import type {
  CreateFormTemplateQuestionRequest,
  FormTemplate,
  FormTemplateQuestion,
} from '../../models/TaskForm/TaskForm';
import { FormAnswerRestriction } from '../../models/TaskForm/TaskForm';

let questionClientId = 0;
const createClientId = () => `q-${questionClientId++}`;

export type EditableTemplateQuestion = CreateFormTemplateQuestionRequest & {
  clientId: string;
};

export const createEmptyTemplateQuestion =
  (): CreateFormTemplateQuestionRequest => ({
    order: 0,
    question: '',
    restriction: FormAnswerRestriction.YesNo,
  });

const asEditableQuestion = (
  question: CreateFormTemplateQuestionRequest,
): EditableTemplateQuestion => ({
  ...question,
  clientId: createClientId(),
});

export const getEditableTemplateQuestions = (
  template: FormTemplate | null,
): CreateFormTemplateQuestionRequest[] => {
  const questions = getTemplateQuestionsInDisplayOrder(template);

  if (!questions.length) {
    return [];
  }

  return questions.map((question, index) => ({
    order: question.order ?? index,
    question: question.question?.value ?? '',
    restriction: question.restriction,
  }));
};

export const getTemplateQuestionsInDisplayOrder = (
  template: FormTemplate | null,
): FormTemplateQuestion[] => {
  if (!template?.questions?.length) {
    return [];
  }

  return template.questions
    .map((question, index) => ({ question, index }))
    .sort((left, right) => {
      const leftOrder = left.question.order ?? left.index + 1;
      const rightOrder = right.question.order ?? right.index + 1;

      return leftOrder - rightOrder || left.index - right.index;
    })
    .map(({ question }) => question);
};

const withSequentialOrder = (
  questions: EditableTemplateQuestion[],
): EditableTemplateQuestion[] =>
  questions.map((question, index) => ({ ...question, order: index + 1 }));

export const reorderTemplateQuestions = (
  questions: EditableTemplateQuestion[],
  fromIndex: number,
  toIndex: number,
): EditableTemplateQuestion[] => {
  if (
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= questions.length ||
    toIndex >= questions.length
  ) {
    return questions;
  }

  const nextQuestions = [...questions];
  const [movedQuestion] = nextQuestions.splice(fromIndex, 1);

  if (!movedQuestion) {
    return questions;
  }

  nextQuestions.splice(toIndex, 0, movedQuestion);

  return withSequentialOrder(nextQuestions);
};

interface UseTemplateQuestionsOptions {
  readonly?: boolean;
}

export const useTemplateQuestions = ({
  readonly = false,
}: UseTemplateQuestionsOptions = {}) => {
  const [questions, setQuestions] = useState<EditableTemplateQuestion[]>([
    asEditableQuestion(createEmptyTemplateQuestion()),
  ]);

  const replaceQuestions = useCallback(
    (nextQuestions: CreateFormTemplateQuestionRequest[]) => {
      setQuestions(withSequentialOrder(nextQuestions.map(asEditableQuestion)));
    },
    [],
  );

  const addQuestion = useCallback(() => {
    if (readonly) return;

    setQuestions((prevQuestions) =>
      withSequentialOrder([
        ...prevQuestions,
        asEditableQuestion(createEmptyTemplateQuestion()),
      ]),
    );
  }, [readonly]);

  const updateQuestion = useCallback(
    (index: number, patch: Partial<CreateFormTemplateQuestionRequest>) => {
      if (readonly) return;

      setQuestions((prevQuestions) =>
        withSequentialOrder(
          prevQuestions.map((question, questionIndex) =>
            questionIndex === index ? { ...question, ...patch } : question,
          ),
        ),
      );
    },
    [readonly],
  );

  const moveQuestion = useCallback(
    (index: number, step: -1 | 1) => {
      if (readonly) return;

      setQuestions((prevQuestions) => {
        const targetIndex = index + step;

        return reorderTemplateQuestions(prevQuestions, index, targetIndex);
      });
    },
    [readonly],
  );

  const reorderQuestions = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (readonly) return;

      setQuestions((prevQuestions) =>
        reorderTemplateQuestions(prevQuestions, fromIndex, toIndex),
      );
    },
    [readonly],
  );

  const copyQuestion = useCallback(
    (index: number) => {
      if (readonly) return;

      setQuestions((prevQuestions) => {
        const questionToCopy = prevQuestions[index];

        if (!questionToCopy) {
          return prevQuestions;
        }

        return withSequentialOrder([
          ...prevQuestions,
          asEditableQuestion({
            order: questionToCopy.order,
            question: questionToCopy.question,
            restriction: questionToCopy.restriction,
          }),
        ]);
      });
    },
    [readonly],
  );

  const deleteQuestion = useCallback(
    (index: number) => {
      if (readonly) return;

      setQuestions((prevQuestions) => {
        if (prevQuestions.length === 1) {
          return prevQuestions;
        }

        return withSequentialOrder(
          prevQuestions.filter((_, questionIndex) => questionIndex !== index),
        );
      });
    },
    [readonly],
  );

  return {
    questions,
    replaceQuestions,
    addQuestion,
    updateQuestion,
    moveQuestion,
    reorderQuestions,
    copyQuestion,
    deleteQuestion,
  };
};
